/**
 * Faceta Operadores do Localizador.
 *
 * `users?search=` casa `name` ∪ `email`, insensitive (engine:
 * `users.service.ts:74-79`). O parâmetro existia no engine e faltava no tipo do
 * front — foi adicionado nesta frente.
 *
 * A meta da linha é o PAPEL, e isso não é decoração: um termo como «caro»
 * devolve a operadora Carolina e a cliente Caroline em seções diferentes, e é a
 * meta que desambigua numa passada de olho. Sem ela as duas linhas ficariam
 * indistinguíveis.
 */
import { useCapability } from '../../hooks/use-capability';
import { useUsers } from '../../hooks/use-users';

import { useDebounced } from './use-debounced';

import type { SpotlightItem } from './types';

export const OPERATOR_MIN_CHARS = 2;
export const OPERATOR_LIMIT = 5;

/** Espelho do catálogo do Topbar. Papel fora do catálogo aparece cru. */
const ROLE_LABELS: Record<string, string> = {
  atendente: 'Atendente',
  supervisor: 'Supervisor',
  gestor: 'Gestor',
  admin_tenant: 'Admin Tenant',
  dpo_tenant: 'DPO',
  suporte_lume: 'Suporte Lume',
  engenharia_lume: 'Engenharia Lume',
  direcao_lume: 'Direção Lume',
};

export function useOperatorHits(term: string): SpotlightItem[] {
  const canRead = useCapability('users-read');
  const debounced = useDebounced(term.trim());
  const enabled = canRead && debounced.length >= OPERATOR_MIN_CHARS;
  const { data } = useUsers(enabled ? { search: debounced, limit: OPERATOR_LIMIT } : {}, enabled);
  if (!enabled) return [];
  return (data?.data ?? []).map((u) => ({
    key: `operator:${u.id}`,
    section: 'operators' as const,
    label: u.name,
    meta: ROLE_LABELS[u.primaryRole] ?? u.primaryRole,
    // A REGRA da paleta (TL, 14/Ago): Enter faz o que o painel mostra como ação
    // primária. Para operador, a primária do card é «Acompanhar» — mandar o
    // Enter para o painel de equipe era a mesma contradição que havia em
    // Pessoas: o botão âmbar dizia uma coisa e a tecla fazia outra.
    to: '/supervisor/acompanhamento',
    // Só o id: a ficha é de duas camadas e busca identidade por id, porque os
    // sinais vivos podem não existir para este operador.
    preview: { kind: 'operator' as const, userId: u.id },
  }));
}
